const MOBILE_MQ = '(max-width: 900px)';
const VIEWPORT_DEFAULT = 'width=device-width, initial-scale=1.0, viewport-fit=cover, maximum-scale=5';
const VIEWPORT_INPUT_LOCK = 'width=device-width, initial-scale=1.0, viewport-fit=cover, maximum-scale=1';
const VIEWPORT_PINCH_LOCK =
  'width=device-width, initial-scale=1.0, viewport-fit=cover, maximum-scale=1, user-scalable=no';
/** Visual viewport shorter than layout by this much ⇒ keyboard likely open. */
const KEYBOARD_GAP_PX = 120;

/** Pages that should not allow pinch-zoom (e.g. choose-portal). */
let pinchLockDepth = 0;

function isMobileViewport() {
  return window.matchMedia(MOBILE_MQ).matches;
}

/** iOS Add-to-Home-Screen / installed PWA — viewport math differs from Safari tabs. */
export function isStandaloneWebApp(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isTextInput(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (
    !(el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement)
  ) {
    return false;
  }
  if (el instanceof HTMLInputElement) {
    const type = (el.type || 'text').toLowerCase();
    return ![
      'checkbox',
      'radio',
      'button',
      'submit',
      'reset',
      'hidden',
      'file',
      'image',
      'range',
      'color',
    ].includes(type);
  }
  return true;
}

function resetDockViewportStyles(nav: HTMLElement | null) {
  if (!nav) return;
  nav.style.removeProperty('transform');
  nav.style.removeProperty('transform-origin');
  nav.style.removeProperty('width');
  nav.style.removeProperty('left');
  nav.style.removeProperty('right');
  nav.style.removeProperty('bottom');
}

/** Largest stable layout height seen while the keyboard is closed. */
let baselineAppHeight = 0;

function readLayoutHeight(): number {
  return Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0);
}

function isKeyboardLikelyOpen(): boolean {
  const vv = window.visualViewport;
  if (!vv) return false;
  const layout = readLayoutHeight();
  // Keyboard: visual height drops; pinch-zoom keeps visual≈layout/scale relationship differently.
  if (Math.abs(vv.scale - 1) >= 0.02) return false;
  return layout - vv.height > KEYBOARD_GAP_PX;
}

function syncAppViewportMetrics() {
  const layout = readLayoutHeight();
  const visual = window.visualViewport?.height ?? 0;
  const keyboardOpen = isKeyboardLikelyOpen();

  if (!keyboardOpen) {
    baselineAppHeight = Math.max(baselineAppHeight, layout, visual || 0);
  }

  // Dock shells: fixed dock is pinned to the physical screen. Never publish a short
  // --app-height left over from choose-portal / Verifying — that shrinks html/body
  // and leaves a white band under the DEM dock (CRM paints before the short value sticks).
  if (document.documentElement.classList.contains('has-dock')) {
    document.documentElement.style.removeProperty('--app-height');
    return;
  }

  // Never shrink the app shell to the keyboard viewport — that leaves a blank band
  // and parks the dock mid-screen after dismiss on iOS.
  const height = keyboardOpen
    ? Math.max(baselineAppHeight, layout)
    : Math.max(baselineAppHeight, layout, visual || 0);

  if (height > 0) {
    document.documentElement.style.setProperty('--app-height', `${Math.round(height)}px`);
  }
}

/**
 * Dock shells use CSS `bottom: 0` only. Never write visualViewport offsets —
 * DEM soft-entry left a large blank under the dock after choose-portal.
 */
function syncDockToVisualViewport() {
  if (!isMobileViewport()) return;
  const nav = document.querySelector('.dock-nav') as HTMLElement | null;
  if (nav) resetDockViewportStyles(nav);
}

/** After keyboard dismiss, iOS often lags — pulse resets over a few frames. */
function recoverAfterKeyboard() {
  const nav = document.querySelector('.dock-nav') as HTMLElement | null;
  resetDockViewportStyles(nav);
  // Nudge scroll so Safari recomputes the layout viewport.
  const x = window.scrollX;
  const y = window.scrollY;
  window.scrollTo(x, y + 1);
  window.scrollTo(x, y);
  syncAppViewportMetrics();
  syncDockToVisualViewport();
  window.dispatchEvent(new Event('bth:viewport-recover'));
}

/** Measure and publish --app-height (same metric portals use for the shell). */
export function syncMobileAppHeight(): void {
  syncAppViewportMetrics();
}

/** Drop inflated --app-height from choose-portal before mounting a dock shell. */
export function resetAppViewportBaseline(): void {
  baselineAppHeight = 0;
  document.documentElement.style.removeProperty('--app-height');
  resetDockViewportStyles(document.querySelector('.dock-nav'));
}

/**
 * Lock the dock height chain BEFORE navigate (DEM JWT wait on chooser).
 * Avoids chooser document-scroll → has-dock swap that flashes full then snaps
 * to a short waterline / blank under the dock.
 */
export function armDockViewportForPortalEntry(): void {
  document.documentElement.classList.remove('has-portal-chooser', 'has-auth-shell');
  document.documentElement.classList.add('has-dock');
  resetAppViewportBaseline();
}

/**
 * choose-portal → portal: one quiet cleanup. No scroll nudge / multi-frame pulse —
 * those looked full-screen briefly then snapped a blank under the DEM dock.
 */
export function recoverMobileViewportAfterPortalEntry(): void {
  armDockViewportForPortalEntry();
  resetDockViewportStyles(document.querySelector('.dock-nav'));
}

/** Tear down chooser/login fixed-#root before soft navigate (same waterline as CRM). */
export function releaseChooserViewportLock(): void {
  document.documentElement.classList.remove('has-portal-chooser', 'has-auth-shell');
  resetAppViewportBaseline();
}

/**
 * Mobile Safari: block auto-zoom on input focus without changing input font sizes,
 * while still allowing manual pinch-zoom when no field is focused.
 */
export function installMobileViewportGuards(): () => void {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return () => {};

  let blurTimer: ReturnType<typeof setTimeout> | null = null;
  let recoverTimers: ReturnType<typeof setTimeout>[] = [];
  let active = false;

  const clearRecoverTimers = () => {
    for (const t of recoverTimers) clearTimeout(t);
    recoverTimers = [];
  };

  const lockInputZoom = () => meta.setAttribute('content', VIEWPORT_INPUT_LOCK);
  const unlockInputZoom = () => {
    // Stay locked while a page (e.g. choose-portal) requested no pinch-zoom.
    if (pinchLockDepth > 0) {
      meta.setAttribute('content', VIEWPORT_PINCH_LOCK);
      return;
    }
    meta.setAttribute('content', VIEWPORT_DEFAULT);
  };

  /** If Safari already auto-zoomed, bounce meta to force scale back to 1. */
  const resetScaleIfNeeded = () => {
    const scale = window.visualViewport?.scale ?? 1;
    if (Math.abs(scale - 1) < 0.02) {
      unlockInputZoom();
      recoverAfterKeyboard();
      return;
    }
    meta.setAttribute('content', VIEWPORT_INPUT_LOCK);
    requestAnimationFrame(() => {
      unlockInputZoom();
      recoverAfterKeyboard();
    });
  };

  const scheduleKeyboardRecover = () => {
    clearRecoverTimers();
    // iOS often settles layout 50–350ms after the keyboard animation ends.
    for (const delay of [0, 50, 150, 350, 600]) {
      recoverTimers.push(setTimeout(() => recoverAfterKeyboard(), delay));
    }
  };

  const onPointerDown = (event: Event) => {
    if (!isMobileViewport() || !isTextInput(event.target)) return;
    // Lock before focus so iOS cannot auto-zoom fields under 16px.
    lockInputZoom();
  };

  const onFocusIn = (event: FocusEvent) => {
    if (!isMobileViewport() || !isTextInput(event.target)) return;
    if (blurTimer) {
      clearTimeout(blurTimer);
      blurTimer = null;
    }
    clearRecoverTimers();
    lockInputZoom();
  };

  const onFocusOut = () => {
    if (!isMobileViewport()) return;
    blurTimer = setTimeout(() => {
      blurTimer = null;
      if (isTextInput(document.activeElement)) return;
      resetScaleIfNeeded();
      scheduleKeyboardRecover();
    }, 120);
  };

  const onViewportChange = () => {
    syncAppViewportMetrics();
    if (!isMobileViewport()) {
      resetDockViewportStyles(document.querySelector('.dock-nav'));
      return;
    }
    syncDockToVisualViewport();
    // When the keyboard fully closes, visual height snaps back — recover once.
    if (!isKeyboardLikelyOpen()) {
      resetDockViewportStyles(document.querySelector('.dock-nav'));
    }
  };

  const enable = () => {
    if (active) return;
    active = true;
    meta.setAttribute('content', VIEWPORT_DEFAULT);
    baselineAppHeight = 0;
    syncAppViewportMetrics();
    document.addEventListener('touchstart', onPointerDown, { passive: true, capture: true });
    document.addEventListener('pointerdown', onPointerDown, { passive: true, capture: true });
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    window.visualViewport?.addEventListener('resize', onViewportChange);
    window.visualViewport?.addEventListener('scroll', onViewportChange);
    window.addEventListener('resize', onViewportChange);
    onViewportChange();
  };

  const disable = () => {
    if (!active) return;
    active = false;
    if (blurTimer) {
      clearTimeout(blurTimer);
      blurTimer = null;
    }
    clearRecoverTimers();
    document.removeEventListener('touchstart', onPointerDown, true);
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('focusout', onFocusOut);
    window.visualViewport?.removeEventListener('resize', onViewportChange);
    window.visualViewport?.removeEventListener('scroll', onViewportChange);
    window.removeEventListener('resize', onViewportChange);
    unlockInputZoom();
    resetDockViewportStyles(document.querySelector('.dock-nav'));
  };

  const mq = window.matchMedia(MOBILE_MQ);
  const onMqChange = () => {
    if (mq.matches) enable();
    else disable();
  };

  onMqChange();
  mq.addEventListener('change', onMqChange);

  return () => {
    mq.removeEventListener('change', onMqChange);
    disable();
  };
}

/**
 * Lock / unlock pinch-zoom on mobile (choose-portal, login shells, etc.).
 * Nested calls are reference-counted; restores the default viewport when depth hits 0.
 * No-op on desktop viewports — pinch meta / lock class are mobile-only.
 */
export function setMobilePinchZoomAllowed(allowed: boolean): () => void {
  if (!isMobileViewport()) return () => {};

  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return () => {};

  if (!allowed) {
    pinchLockDepth += 1;
    if (pinchLockDepth === 1) {
      document.documentElement.classList.add('pinch-zoom-locked');
      meta.setAttribute('content', VIEWPORT_PINCH_LOCK);
    }
    return () => {
      pinchLockDepth = Math.max(0, pinchLockDepth - 1);
      if (pinchLockDepth === 0) {
        document.documentElement.classList.remove('pinch-zoom-locked');
        if (isTextInput(document.activeElement)) {
          meta.setAttribute('content', VIEWPORT_INPUT_LOCK);
        } else {
          meta.setAttribute('content', VIEWPORT_DEFAULT);
        }
      }
    };
  }

  // Explicit unlock of one lock level (same as cleanup).
  pinchLockDepth = Math.max(0, pinchLockDepth - 1);
  if (pinchLockDepth === 0) {
    document.documentElement.classList.remove('pinch-zoom-locked');
    meta.setAttribute('content', VIEWPORT_DEFAULT);
  }
  return () => {};
}
